import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ErrorMessage, Formik } from "formik";
import axios from "../../../axios-instance";
import * as Yup from "yup";
import setAuthToken from "../../../utils/setAuthToken";
import { notification } from "../../../utils/utility";
import { getAttributionIds } from "../../../utils/attribution";
import visibility from "../../../assets/icons/visibility.svg";
import visibilityOff from "../../../assets/icons/visibility_off.svg";

import { getTimezones } from "../../../actions/companyActions";
import { connect } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import FormField from "../../../components/FormField/FormField";
import { emailRegex, getUserBrowserTimezone } from "../../../utils/utility";
import { buildSocialAuthUrl } from "../../../utils/socialAuth";
import SocialAuthButtons from "../../../components/SocialAuthButtons/SocialAuthButtons";
import AuthBrandPanel from "../../../components/AuthBrandPanel/AuthBrandPanel";

let initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  confirm_password: "",
  timezone_id: "",
};

const AuthCloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 3.5l9 9M12.5 3.5l-9 9"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const Signup = (props) => {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] =
    useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [formValues, setFormValues] = useState(null);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const Schema = Yup.object().shape({
    first_name: Yup.string().required("First name is required."),
    last_name: Yup.string().required("Last name is required."),
    email: Yup.string()
      .email("Invalid email")
      .matches(emailRegex, "Please enter a valid email")
      .required("Email is required."),
    password: Yup.string().required("Password is required."),
    confirm_password: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        "Password and confirm password must match"
      )
      .required("Confirm password is required."),
    timezone_id: Yup.string().required("Timezone is required."),
  });

  useEffect(() => {
    const { getTimezones } = props;
    setPageLoading(true);
    getTimezones({
      data: {},
      onSuccess: function (data) {
        setPageLoading(false);
        setTimezones(data.timezones);

        const userBrowserTimezone = getUserBrowserTimezone();
        data.timezones.forEach((timezone) => {
          if (userBrowserTimezone === timezone.timezone_value) {
            setFormValues({
              first_name: "",
              last_name: "",
              email: "",
              password: "",
              confirm_password: "",
              timezone_id: timezone.value,
            });
          }
        });
      },
      onError: function (error) {
        console.log("error", error);
      },
    });
  }, []);

  const formikProps = {
    validationSchema: Schema,
    onSubmit: (values, formikHelpers) => {
      const referralCode = searchParams.get("referral");
      if (referralCode) {
        values["referral"] = referralCode;
      }

      const attributionIds = getAttributionIds();
      if (attributionIds.anonymous_id) {
        values["anonymous_id"] = attributionIds.anonymous_id;
      }
      if (attributionIds.session_id) {
        values["session_id"] = attributionIds.session_id;
      }

      setPageLoading(true);
      axios
        .post("/admin/register", values)
        .then((response) => {
          const data = response.data.data || {};

          if (data.needs_email_verification) {
            setPageLoading(false);
            const emailParam = encodeURIComponent(
              data.email || values.email || ""
            );
            navigate(`/confirmEmail?email=${emailParam}`);
            notification.success("Check your inbox to confirm your email.");
            return;
          }

          const { access_token, jwt_custom_claims } = data;
          setAuthToken(access_token);
          localStorage.setItem("jwtToken", access_token);
          localStorage.setItem("config", JSON.stringify(jwt_custom_claims));
          localStorage.setItem("userTimezone", jwt_custom_claims.timezone);
          localStorage.setItem("userTimezoneId", jwt_custom_claims.timezone_id);
          localStorage.setItem("page", jwt_custom_claims.page);
          const businessIds = Object.values(jwt_custom_claims.businesses || {});
          if (businessIds.length > 0) {
            localStorage.setItem("businessId", businessIds.pop());
          }
          navigate("/signup/profile-step-one");
          notification.success("User Signup Successfully");
        })
        .catch((error) => {
          setPageLoading(false);
          if (error.response?.data?.code === 254) {
            formikHelpers.setFieldError("email", error.response.data.message);
          }
        });
    },
  };
  const changePasswordVisibility = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const changeConfirmPasswordVisibility = () => {
    setConfirmPasswordVisibility(!confirmPasswordVisibility);
  };

  const startSocialSignup = (provider, timezoneId) => {
    const referralCode = searchParams.get("referral");
    window.location.href = buildSocialAuthUrl(provider, "signup", {
      timezone_id: timezoneId || formValues?.timezone_id || undefined,
      referral: referralCode || undefined,
    });
  };

  return (
    <>
      <Loader isShowLoader={pageLoading} />

      <Formik
        {...formikProps}
        initialValues={formValues || initialValues}
        enableReinitialize
      >
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur } = formik;

          return (
            <div className="signup-process auth-page">
              <AuthBrandPanel mode="signup" />
              <div className="signup">
                <div className="cross-btn">
                  <Link to="/" className="auth-close" aria-label="Close">
                    <AuthCloseIcon />
                  </Link>
                </div>
                <div className="signup-form">
                  <form onSubmit={handleSubmit}>
                    <div className="auth-form-header">
                      <p className="auth-form-header__eyebrow">Free account</p>
                      <h1>Create your workspace</h1>
                      <p className="r-p">
                        Set up MeetOcto for your business — no credit card
                        required. Standard and Teams include a 14-day trial
                        when you add a card.
                      </p>
                    </div>
                    <div className="form-groups">
                      <div className="form-group">
                        <label className="same-label" htmlFor="first_name">
                          First name
                        </label>
                        <input
                          type="text"
                          className="form-control same-input"
                          name="first_name"
                          id="first_name"
                          placeholder="Jane"
                          autoComplete="given-name"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          name="first_name"
                          render={(error) => (
                            <span className="error">{error}</span>
                          )}
                        />
                      </div>
                      <div className="form-group">
                        <label className="same-label" htmlFor="last_name">
                          Last name
                        </label>
                        <input
                          type="text"
                          className="form-control same-input"
                          name="last_name"
                          id="last_name"
                          placeholder="Smith"
                          autoComplete="family-name"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          name="last_name"
                          render={(error) => (
                            <span className="error">{error}</span>
                          )}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="same-label" htmlFor="email">
                        Work email
                      </label>
                      <input
                        type="email"
                        className="form-control same-input"
                        name="email"
                        id="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="email"
                        render={(error) => (
                          <span className="error">{error}</span>
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <label className="same-label" htmlFor="password">
                        Password
                      </label>
                      <div className="password-group">
                        <input
                          type={passwordVisibility ? "text" : "password"}
                          className="form-control same-input"
                          name="password"
                          id="password"
                          placeholder="Create a password"
                          autoComplete="new-password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {!passwordVisibility && (
                          <img
                            src={visibilityOff}
                            onClick={changePasswordVisibility}
                            alt=""
                          />
                        )}
                        {passwordVisibility && (
                          <img
                            src={visibility}
                            onClick={changePasswordVisibility}
                            alt=""
                          />
                        )}
                      </div>
                      <ErrorMessage
                        name="password"
                        render={(error) => (
                          <span className="error">{error}</span>
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <label className="same-label" htmlFor="confirm_password">
                        Confirm password
                      </label>
                      <div className="password-group">
                        <input
                          type={confirmPasswordVisibility ? "text" : "password"}
                          className="form-control same-input"
                          name="confirm_password"
                          id="confirm_password"
                          placeholder="Confirm your password"
                          autoComplete="new-password"
                          value={values.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {!confirmPasswordVisibility && (
                          <img
                            src={visibilityOff}
                            onClick={changeConfirmPasswordVisibility}
                            alt=""
                          />
                        )}
                        {confirmPasswordVisibility && (
                          <img
                            src={visibility}
                            onClick={changeConfirmPasswordVisibility}
                            alt=""
                          />
                        )}
                      </div>
                      <ErrorMessage
                        name="confirm_password"
                        render={(error) => (
                          <span className="error">{error}</span>
                        )}
                      />
                    </div>

                    <div className="form-group">
                      <label className="same-label" htmlFor="timezone_id">
                        Timezone
                      </label>
                      <FormField
                        placeholder="Select timezone"
                        showPlaceholder={true}
                        type="select"
                        name="timezone_id"
                        errors={{}}
                        touched={{}}
                        value={values.timezone_id.toString()}
                        options={timezones}
                        elementclasses="same-input"
                      />
                      <ErrorMessage
                        name="timezone_id"
                        render={(error) => (
                          <span className="error">{error}</span>
                        )}
                      />
                    </div>

                    <button type="submit" className="btn btn-app">
                      Create account
                    </button>
                    <SocialAuthButtons
                      onGoogle={() =>
                        startSocialSignup("google", values.timezone_id)
                      }
                      onMicrosoft={() =>
                        startSocialSignup("microsoft", values.timezone_id)
                      }
                    />
                    <p className="login-here">
                      Already have an account?{" "}
                      <Link to="/admin/login">Sign in</Link>
                    </p>
                  </form>
                </div>
                <div className="signup-footer">
                  <p>© {new Date().getFullYear()} MeetOcto. A JB Media LLC company. All Rights Reserved.</p>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default connect(null, {
  getTimezones,
})(Signup);
